import React from 'react';
import { ProSidebarProvider } from 'react-pro-sidebar';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Calculator from './components/utilities/calculator/Calculator';
import Converter from './components/utilities/converter/Converter';
import GraphPage from './components/utilities/graph/GraphPage';
import EncodersDecoders from './components/utilities/encoders/EncodersDecoders';
import JsonUtility from './components/utilities/json/JsonUtility';
import TimeUtility from './components/utilities/time/TimeUtility';
import TextDiff from './components/utilities/text/TextDiff';
import Home from './components/pages/Home';
import Sidebar from './components/layout/Sidebar';
import Card from './components/common/Card';
import { ThemeProvider } from './context/ThemeContext';
import ThemeToggle from './components/common/ThemeToggle';
//@ts-ignore
import { 
  FaCalculator, 
  FaRuler, 
  FaChartLine, 
  FaCode, 
  FaFileCode, 
  FaImage,
  FaTools,
  FaFileAlt,
  FaPalette,
  FaRandom,
  FaClock,
  FaExchangeAlt
} from 'react-icons/fa';
import AsciiArtGenerator from './components/utilities/ascii/AsciiArtGenerator';
import ImageProcessor from './components/utilities/image/ImageProcessor';

// Utility components will be imported here
// import Calculator from './components/utilities/Calculator';

// Remove time utility from categories
const utilityCategories = [
  {
    id: 'math',
    name: 'Math & Numbers',
    icon: FaCalculator,
    items: [
      { id: 'calculator', name: 'Calculator', path: '/math/calculator', icon: FaCalculator },
      { id: 'converter', name: 'Unit Converter', path: '/math/converter', icon: FaRuler },
      { id: 'graph', name: 'Graph Plotter', path: '/math/graph', icon: FaChartLine },
    ]
  },
  {
    id: 'text',
    name: 'Text Tools',
    icon: FaFileAlt,
    items: [
      { id: 'encoders', name: 'Encoders/Decoders', path: '/text/encoders', icon: FaCode },
      { id: 'json', name: 'JSON Utility', path: '/text/json', icon: FaFileCode },
      { id: 'diff', name: 'Text Diff', path: '/text/diff', icon: FaExchangeAlt },
    ]
  },
  {
    id: 'time',
    name: 'Time Tools',
    icon: FaClock,
    items: [
      { id: 'time', name: 'Time Utility', path: '/time', icon: FaClock },
    ]
  },
  {
    id: 'art',
    name: 'Art & Design',
    icon: FaPalette,
    items: [
      { id: 'ascii', name: 'ASCII Art', path: '/art/ascii', icon: FaImage },
      { id: 'image', name: 'Image Processor', path: '/art/image', icon: FaImage },
    ]
  },
  {
    id: 'other',
    name: 'Other Tools',
    icon: FaTools,
    items: []
  }
];

// Separate AppContent component to use hooks
const AppContent: React.FC = () => {
  const location = useLocation();
  
  return (
    <div className="flex h-screen bg-white dark:bg-gray-900 transition-colors">
      <Sidebar categories={utilityCategories} />
      <main className="flex-1 p-8 overflow-auto bg-gray-50 dark:bg-gray-800">
        <div className="flex justify-end mb-4">
          <ThemeToggle />
        </div>
        <Card variant="default" noPadding={location.pathname === '/text/json' || location.pathname === '/text/diff'}>
          <Routes>
            <Route path="/" element={<Home />} />
            
            {/* Math & Numbers Routes */}
            <Route path="/math">
              <Route path="calculator" element={<Calculator />} />
              <Route path="converter" element={<Converter />} />
              <Route path="graph" element={<GraphPage />} />
            </Route>

            {/* Text Tools Routes */}
            <Route path="/text">
              <Route path="encoders" element={<EncodersDecoders />} />
              <Route path="json" element={<JsonUtility />} />
              <Route path="diff" element={<TextDiff />} />
            </Route>

            {/* Time Tools Routes */}
            <Route path="/time" element={<TimeUtility />} />

            {/* Art & Design Routes */}
            <Route path="/art">
              <Route path="ascii" element={<AsciiArtGenerator />} />
              <Route path="image" element={<ImageProcessor />} />
            </Route>

            {/* Redirect any unmatched routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Card>
      </main>
    </div>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <ProSidebarProvider>
          <AppContent />
        </ProSidebarProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
